import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fromJS, List } from 'immutable';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { TicketKey } from 'flow/types/EntityKeys';
import { EmailRecipient } from 'flow/types/TicketEventData';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOriginDTOSubtype } from 'flow/selectors/TicketsSelectors';
import { makeSelectMostRecentIncomingEmailKey } from 'flow/selectors/TicketEventSelectors';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { fetchEmailRecipients } from 'flow/components/tickets/Input/InputActions';
import EmailRecipientsPopoverPanel from './EmailRecipientsPopoverPanel';
import Skeleton from 'flow/components/ui-components/Skeleton';

interface Props {
    ticketKey: TicketKey;
}

const BannerEmailRecipient = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const originSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );
    const selectMostRecentIncomingEmailKey = useMemo(
        makeSelectMostRecentIncomingEmailKey,
        [],
    );
    const mostRecentIncomingEmailKey = useAppSelector((state) =>
        selectMostRecentIncomingEmailKey(state, ticketKey),
    );
    const [recipients, setRecipients] = useState<EmailReplyRecipients | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);
    const hasFetchedOnce = useRef(false);

    useEffect(() => {
        if (originSubtype !== 'email') return;

        if (!hasFetchedOnce.current) setIsLoading(true);
        fetchEmailRecipients(ticketKey)
            .then((data) => {
                setRecipients({
                    toRecipients: data.toRecipients
                        ? fromJS(data.toRecipients)
                        : List(),
                    ccRecipients: data.ccRecipients
                        ? fromJS(data.ccRecipients)
                        : List(),
                });
            })
            .finally(() => {
                setIsLoading(false);
                hasFetchedOnce.current = true;
            });
    }, [ticketKey, originSubtype, mostRecentIncomingEmailKey]);

    if (originSubtype !== 'email') return null;
    if (isLoading) return <BannerSkeleton />;
    if (!recipients) return null;

    const bannerLabel = getBannerLabel(recipients, i18next);
    if (!bannerLabel) return null;

    return (
        <div className="flex h-8 w-full items-center rounded-ss-sm rounded-se-sm bg-gray-100 ps-2 text-xs text-gray-500">
            <Popover className="relative w-full min-w-0">
                <PopoverButton className="cursor-pointer truncate text-gray-400 hover:text-gray-600">
                    {bannerLabel}
                </PopoverButton>
                <PopoverPanel className="absolute bottom-full z-50 mb-1 max-w-full">
                    <EmailRecipientsPopoverPanel
                        toRecipients={recipients.toRecipients}
                        ccRecipients={recipients.ccRecipients}
                    />
                </PopoverPanel>
            </Popover>
        </div>
    );
};

export default React.memo(BannerEmailRecipient);

export interface EmailReplyRecipients {
    toRecipients: List<TypedMap<EmailRecipient>>;
    ccRecipients: List<TypedMap<EmailRecipient>>;
}

export const getBannerLabel = (
    recipients: EmailReplyRecipients,
    i18next: { t: (key: string, options?: Record<string, unknown>) => string },
): string => {
    const toCount = recipients.toRecipients.size;
    const ccCount = recipients.ccRecipients.size;

    if (toCount === 0) {
        // No recipients — hide banner (Edge case #1)
        if (ccCount === 0) return '';
        // 0 to, N cc — "Replying to 2 CC recipients" (Edge case #2)
        return i18next.t('tickets.message.ccRecipientCount', {
            count: ccCount,
        });
    }

    if (toCount === 1) {
        const firstTo = recipients.toRecipients.first();
        const recipientLabel = firstTo.get('address') || firstTo.get('name');
        // 1 to, 0 cc — "Replying to alice@example.com"
        if (ccCount === 0) {
            return i18next.t('tickets.message.to', {
                recipients: recipientLabel,
            });
        }
        // 1 to, N cc — "Replying to alice@example.com +2 CC"
        return i18next.t('tickets.message.toWithCcCount', {
            recipient: recipientLabel,
            count: ccCount,
        });
    }

    // N to, M cc — "Replying to 4 recipients"
    return i18next.t('tickets.message.toRecipientCount', {
        count: toCount + ccCount,
    });
};

const BannerSkeleton = () => (
    <div className="flex h-8 w-full items-center rounded-ss-sm rounded-se-sm bg-gray-100 ps-2">
        <Skeleton>
            <Skeleton.Text className="w-48" />
        </Skeleton>
    </div>
);
